// ~~~ helper.ts
// SOURCE: https://gist.github.com/ilkou/7bf2dbd42a7faf70053b43034fc4b5a4
import {
  type ClassNamesConfig,
  type GroupBase,
  type StylesConfig,
} from 'react-select';

import { cn } from '~shared/shadcn/lib/utils';

/**
 * styles that aligns with shadcn/ui
 */
const controlStyles = {
  base: 'flex !min-h-9 w-full rounded-md border border-input bg-transparent pl-3 py-1 pr-1 gap-1 text-sm shadow-sm transition-colors hover:cursor-pointer',
  focus: 'outline-none ring-1 ring-ring',
  disabled: 'cursor-not-allowed opacity-50',
};
const placeholderStyles = 'text-sm text-muted-foreground';
const valueContainerStyles = 'gap-1';
const multiValueStyles =
  'inline-flex items-center gap-2 rounded-md border border-transparent bg-primary text-primary-foreground hover:bg-primary/80 px-1.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';
const indicatorsContainerStyles = 'gap-1';
const clearIndicatorStyles = 'p-1 rounded-md';
const indicatorSeparatorStyles = 'bg-border';
const dropdownIndicatorStyles = 'p-1 rounded-md';
const menuStyles =
  'p-1 mt-1 border bg-popover shadow-md rounded-md text-popover-foreground';
const groupHeadingStyles =
  'py-2 px-1 text-secondary-foreground text-sm font-semibold';
const optionStyles = {
  base: 'hover:cursor-pointer hover:bg-accent hover:text-accent-foreground px-2 py-1.5 rounded-sm !text-sm !cursor-default !select-none !outline-none font-sans',
  focus: 'active:bg-accent/90 bg-accent text-accent-foreground',
  disabled: 'pointer-events-none opacity-50',
  selected: '',
};
const noOptionsMessageStyles =
  'text-accent-foreground p-2 bg-accent border border-dashed border-border rounded-sm';
const loadingIndicatorStyles =
  'flex items-center justify-center h-4 w-4 opacity-50';
const loadingMessageStyles = 'text-accent-foreground p-2 bg-accent';

/**
 * This factory method is used to build custom classNames configuration
 */
export const createClassNames = (
  classNames: ClassNamesConfig,
): ClassNamesConfig => {
  return {
    clearIndicator: (state) =>
      cn(clearIndicatorStyles, classNames.clearIndicator?.(state)),
    container: (state) => cn(classNames.container?.(state)),
    control: (state) =>
      cn(
        controlStyles.base,
        state.isDisabled && controlStyles.disabled,
        state.isFocused && controlStyles.focus,
        classNames.control?.(state),
      ),
    dropdownIndicator: (state) =>
      cn(dropdownIndicatorStyles, classNames.dropdownIndicator?.(state)),
    group: (state) => cn(classNames.group?.(state)),
    groupHeading: (state) =>
      cn(groupHeadingStyles, classNames.groupHeading?.(state)),
    indicatorsContainer: (state) =>
      cn(indicatorsContainerStyles, classNames.indicatorsContainer?.(state)),
    indicatorSeparator: (state) =>
      cn(indicatorSeparatorStyles, classNames.indicatorSeparator?.(state)),
    input: (state) => cn(classNames.input?.(state)),
    loadingIndicator: (state) =>
      cn(loadingIndicatorStyles, classNames.loadingIndicator?.(state)),
    loadingMessage: (state) =>
      cn(loadingMessageStyles, classNames.loadingMessage?.(state)),
    menu: (state) => cn(menuStyles, classNames.menu?.(state)),
    menuList: (state) =>
      cn('overscroll-contain', classNames.menuList?.(state)),
    menuPortal: (state) => cn(classNames.menuPortal?.(state)),
    multiValue: (state) => cn(multiValueStyles, classNames.multiValue?.(state)),
    multiValueLabel: (state) => cn(classNames.multiValueLabel?.(state)),
    multiValueRemove: (state) => cn(classNames.multiValueRemove?.(state)),
    noOptionsMessage: (state) =>
      cn(noOptionsMessageStyles, classNames.noOptionsMessage?.(state)),
    option: (state) =>
      cn(
        optionStyles.base,
        state.isFocused && optionStyles.focus,
        state.isDisabled && optionStyles.disabled,
        state.isSelected && optionStyles.selected,
        classNames.option?.(state),
      ),
    placeholder: (state) =>
      cn(placeholderStyles, classNames.placeholder?.(state)),
    singleValue: (state) => cn(classNames.singleValue?.(state)),
    valueContainer: (state) =>
      cn(valueContainerStyles, classNames.valueContainer?.(state)),
  };
};
export const defaultClassNames = createClassNames({});

/** Marker class on portaled react-select menus (Radix dismiss / z-index helpers). */
export const SELECT_MENU_PORTAL_CLASS = 'enrollio-select-menu-portal';

/**
 * Sticky EnrollioTable headers use `z-10`. react-select's portaled menu defaults
 * to `zIndex: 1`, so the header (e.g. "Actions") paints over the open list.
 */
export const SELECT_MENU_LAYER_Z_INDEX = 9999;

export const defaultStyles: StylesConfig<
  unknown,
  boolean,
  GroupBase<unknown>
> = {
  input: (base) => ({
    ...base,
    fontSize: 16,
    'input:focus': {
      boxShadow: 'none',
    },
  }),
  multiValueLabel: (base) => ({
    ...base,
    whiteSpace: 'normal',
    overflow: 'visible',
  }),
  control: (base) => ({
    ...base,
    transition: 'none',
    // minHeight: '2.25rem', // we used !min-h-9 instead
  }),
  menu: (base) => ({
    ...base,
    zIndex: SELECT_MENU_LAYER_Z_INDEX,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: SELECT_MENU_LAYER_Z_INDEX,
  }),
  menuList: (base) => ({
    ...base,
    overscrollBehavior: 'contain',
    '::-webkit-scrollbar': {
      width: 8,
      background: 'transparent',
    },
    '::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '::-webkit-scrollbar-thumb': {
      background: 'hsl(var(--muted-foreground) / 0.35)',
      borderRadius: 4,
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: 'hsl(var(--muted-foreground) / 0.55)',
    },
  }),
  multiValue: (base) => ({
    ...base,
    background: 'hsl(var(--primary))',
    ':hover': {
      background: 'hsl(var(--primary)/0.6)',
    },
  }),
};

type SelectStyles = StylesConfig<unknown, boolean, GroupBase<unknown>>;

/**
 * Keep the menu above sticky table headers even when a caller overrides styles
 * and omits `menuPortal.zIndex`.
 */
export function withSelectMenuLayerStyles(
  styles: SelectStyles = defaultStyles,
): SelectStyles {
  return {
    ...styles,
    menu: (base, props) => {
      const fromCaller =
        typeof styles.menu === 'function' ? styles.menu(base, props) : {};
      return {
        ...base,
        ...fromCaller,
        zIndex: SELECT_MENU_LAYER_Z_INDEX,
      };
    },
    menuPortal: (base, props) => {
      const fromCaller =
        typeof styles.menuPortal === 'function'
          ? styles.menuPortal(base, props)
          : {};
      return {
        ...base,
        ...fromCaller,
        zIndex: SELECT_MENU_LAYER_Z_INDEX,
      };
    },
  };
}

/**
 * ENR-4012: Radix Dialog/AlertDialog treat body-portaled menus as outside
 * clicks. Detect the modal layer from a mounted host node.
 */
export function isNodeInsideDismissableModal(
  node: Element | null | undefined,
): boolean {
  if (!node || typeof node.closest !== 'function') return false;
  return Boolean(
    node.closest('[role="dialog"]') ||
      node.closest('[role="alertdialog"]') ||
      node.closest('[data-radix-dialog-content]') ||
      node.closest('[data-radix-alert-dialog-content]'),
  );
}

/**
 * Resolve react-select `menuPortalTarget`.
 * - Explicit `menuPortalTarget` (including `null`) always wins.
 * - Inside a Radix dialog: never default to `document.body` (F6).
 * - Outside dialogs: body-portal by default (sticky table headers) or when
 *   `menuInPortal` is true; `menuInPortal: false` disables the portal.
 */
export function resolveSelectMenuPortalTarget(params: {
  menuPortalTarget?: HTMLElement | null;
  menuInPortal?: boolean;
  isInsideDialog: boolean;
}): HTMLElement | null {
  if (params.menuPortalTarget !== undefined) {
    return params.menuPortalTarget;
  }
  if (params.isInsideDialog) {
    return null;
  }
  if (params.menuInPortal === false) {
    return null;
  }
  if (typeof document === 'undefined') {
    return null;
  }
  return document.body;
}
